import { Map, fromJS, Set, List } from 'immutable';
import { SESSION_NAME } from 'constants';
import * as actionTypes from '../actions/actionTypes';
import { getLocalSession, storeParamsTo } from './helper';

export default function (
    storage,
    docViewer = false,
    onWidgetEvent = {},
    navigate,
    emitApplicationEvent
) {
    const initialState = Map({
        connected: false,
        initialized: false,
        isChatVisible: true,
        isChatOpen: false,
        disabledInput: true,
        docViewer,
        unreadCount: 0,
        messageDelayed: false,
        oldUrl: '',
        pageChangeCallbacks: Map(),
    });

    return function reducer(state = initialState, action) {
        const storeParams = storeParamsTo(storage);
        switch (action.type) {
            // Each change to the redux store's behavior Map gets recorded to storage
            case actionTypes.SHOW_CHAT: {
                if (onWidgetEvent.onChatVisible) onWidgetEvent.onChatVisible();
                return storeParams(state.update('isChatVisible', () => true));
            }
            case actionTypes.HIDE_CHAT: {
                if (onWidgetEvent.onChatHidden) onWidgetEvent.onChatHidden();
                return storeParams(state.update('isChatVisible', () => false));
            }
            case actionTypes.TOGGLE_CHAT: {
                if (state.get('isChatOpen', false)) {
                    onWidgetEvent.onChatClose && onWidgetEvent.onChatClose();
                } else {
                    onWidgetEvent.onChatOpen && onWidgetEvent.onChatOpen();
                }

                return storeParams(
                    state.update('isChatOpen', (isChatOpen) => !isChatOpen).set('unreadCount', 0)
                );
            }
            case actionTypes.OPEN_CHAT: {
                if (onWidgetEvent.onChatOpen) onWidgetEvent.onChatOpen();
                return storeParams(state.update('isChatOpen', () => true).set('unreadCount', 0));
            }
            case actionTypes.CLOSE_CHAT: {
                if (onWidgetEvent.onChatClose) onWidgetEvent.onChatClose();
                return storeParams(state.update('isChatOpen', () => false));
            }
            case actionTypes.TOGGLE_FULLSCREEN: {
                if (state.get('fullScreenMode', false)) {
                    onWidgetEvent.onChatNoFullScreen && onWidgetEvent.onChatNoFullScreen();
                } else {
                    onWidgetEvent.onChatFullScreen && onWidgetEvent.onChatFullScreen();
                }

                return storeParams(
                    state.update('fullScreenMode', (fullScreenMode) => !fullScreenMode)
                );
            }
            case actionTypes.SET_FULLSCREEN: {
                // Idempotent counterpart of TOGGLE_FULLSCREEN: the mobile takeover
                // enters on every composer focus and exits on the toggle, and must
                // not flip the flag or fire the host callbacks on a no-op.
                const enabled = !!action.enabled;
                if (state.get('fullScreenMode', false) === enabled) {
                    return state;
                }
                if (enabled) {
                    onWidgetEvent.onChatFullScreen?.();
                } else {
                    onWidgetEvent.onChatNoFullScreen?.();
                }
                return storeParams(state.set('fullScreenMode', enabled));
            }
            case actionTypes.SET_LARGECHATSIZE: {
                return state.set('largeChatSize', action.enabled);
            }
            case actionTypes.TOGGLE_INPUT_DISABLED: {
                const disable = action.disable;
                if (disable !== undefined && disable !== null) {
                    return storeParams(state.update('disabledInput', () => disable));
                }

                return storeParams(
                    state.update('disabledInput', (disabledInput) => !disabledInput)
                );
            }
            case actionTypes.CONNECT: {
                return storeParams(state.set('connected', true).set('disabledInput', false));
            }
            case actionTypes.DISCONNECT: {
                return storeParams(state.set('connected', false).set('disabledInput', true));
            }
            case actionTypes.INITIALIZE: {
                return storeParams(state.set('initialized', true));
            }
            case actionTypes.NEW_UNREAD_MESSAGE: {
                return storeParams(state.set('unreadCount', state.get('unreadCount', 0) + 1));
            }
            case actionTypes.TRIGGER_MESSAGE_DELAY: {
                return storeParams(state.set('messageDelayed', action.messageDelayed));
            }
            case actionTypes.SET_OLD_URL: {
                return storeParams(state.set('oldUrl', action.url));
            }
            case actionTypes.SET_PAGECHANGE_CALLBACKS: {
                return storeParams(
                    state.set('pageChangeCallbacks', fromJS(action.pageChangeCallbacks))
                );
            }
            case actionTypes.EVAL_URL: {
                const newUrl = action.url;
                const pageCallbacks = state.get('pageChangeCallbacks');
                const pageCallbacksJs = pageCallbacks ? pageCallbacks.toJS() : {};
                if (!pageCallbacksJs.pageChanges) return state;
                if (state.get('oldUrl') !== newUrl) {
                    return storeParams(
                        state.set('oldUrl', newUrl).set('pageChangeCallbacks', Map())
                    );
                }
                return state;
            }

            // Pull params from storage to redux store
            case actionTypes.PULL_SESSION: {
                const localSession = getLocalSession(storage, SESSION_NAME);

                // Do not persist connected state
                const connected = state.get('connected');
                const messageDelayed = state.get('messageDelayed');
                if (localSession && localSession.params) {
                    return fromJS({ ...localSession.params, connected, messageDelayed }).update(
                        'showingGreetings',
                        (v) => v && Set(v)
                    );
                }
                return state;
            }

            case actionTypes.NAVIGATE: {
                navigate &&
                    action &&
                    action.payload &&
                    navigate(action.payload.urlName, action.payload.urlParams);
                return state;
            }

            case actionTypes.APPLICATION_EVENT: {
                emitApplicationEvent &&
                    action &&
                    action.event &&
                    emitApplicationEvent(action.event, action.payload);
                return state;
            }

            case actionTypes.WIDGET_EVENT: {
                const handler =
                    onWidgetEvent && action && action.event && onWidgetEvent[action.event];
                handler && handler(action.payload);
                return state;
            }

            case actionTypes.SET_GREETINGS: {
                return state.set('pendingGreetings', List(action.greetings));
            }

            case actionTypes.SET_GREETINGS_DELAY: {
                return state.set('greetingsDelayMs', action.delayMs);
            }

            case actionTypes.SHOW_GREETING: {
                return state.update('showingGreetings', Set(), (set) => set.add(action.id));
            }

            case actionTypes.HIDE_GREETING: {
                return state.update('showingGreetings', Set(), (set) => set.remove(action.id));
            }

            case actionTypes.HIDE_ALL_GREETINGS: {
                return state.set('showingGreetings', Set());
            }

            default:
                return state;
        }
    };
}
